<!-- @format -->

<template>
	<div style="display: flex; gap: 8px; height: 100%">
		<div :id="renderId" :style="{ flex: 1, width: isMobile ? '100%' : '', overflow: 'hidden auto' }">
			<el-form
				id="pc-form-render"
				ref="formRef"
				:model="formDatas"
				:class="['pc-form-render', formAttribute.labelEllipsis ? 'form-ellipsis-label' : '']"
				v-bind="formAttribute"
				scroll-to-error
				:label-width="labelWidth"
				@submit.prevent
			>
				<form-widget-render v-model="formDatas" :widget-datas="deferWidgetDatas" />
			</el-form>
		</div>
		<div v-if="!isMobile && formAttribute.hasAnchor" class="hidden-xs-only">
			<pc-anchor
				:data="anchorList"
				:get-id="(itm) => itm.code"
				:render-id="renderId"
				:scroll="isWindowScroll ? undefined : scroll || `#${renderId}`"
				:get-label="(itm) => itm.pcProps.label"
			/>
		</div>
	</div>
</template>

<script setup lang="ts">
	import Script from '../utils/script';
	import { getWidgetItem } from '../utils/transWidgetDatas';
	import { useBaseConfigStoreWithOut } from '../config/widget';
	import formWidgetRender from './form-widget-render.vue';
	import type { FieldsType, SetBaseConfig, ScriptFuncType } from '../types';
	import type { FormInstance } from 'element-plus';
	import { PcAnchor } from '@/components';
	import { useDeferSetArray } from '@/utils/defer';

	interface Props {
		widgetDatas: FieldsType[];
		modelValue: Record<string, string>;
		formAttribute: Record<string, any>;
		otherFunc?: ScriptFuncType;
		setBaseConfig?: SetBaseConfig;
		disabled?: boolean;
		formStatus?: 'widget' | 'render' | 'preview';
		name?: string;
		isMobile?: boolean;
		fieldActionConfig?: Record<string, Function>;
		hiddenAnchor?: string[];
		scroll?: string;
		isWindowScroll?: boolean;
		loadFinishFunc?: Function;
		deferSetWidgetDatas?: boolean;
	}

	const props = withDefaults(defineProps<Props>(), {
		fieldActionConfig: () => ({}),
		name: '',
		deferSetWidgetDatas: true,
	});

	const emit = defineEmits(['update:modelValue', 'update:widgetDatas']);

	const formRef = ref<FormInstance | null>(null);

	const { cancel, setDeferArray } = useDeferSetArray();

	const renderId = computed(() => `form-render-${props.name}`);

	const labelWidth = computed(() =>
		props.formAttribute.labelWidth && props.formAttribute.labelPosition !== 'top'
			? `${props.formAttribute.labelWidth}px`
			: 'auto',
	);

	const labelPosition = computed(() => (props.formAttribute.labelPosition === 'right' ? 'end' : 'start'));

	const formDatas = computed({
		get() {
			return props.modelValue || {};
		},
		set(value) {
			emit('update:modelValue', value);
		},
	});

	const widgetDatas = computed({
		get() {
			return props.widgetDatas || [];
		},
		set(value) {
			emit('update:widgetDatas', value);
		},
	});

	const deferWidgetDatas = ref<FieldsType[]>([]);

	const anchorList = computed(() =>
		widgetDatas.value.filter(
			(itm) => itm.category === 'container' && !itm.pcProps.hidden && !props.hiddenAnchor?.includes(itm.code),
		),
	);

	const disabled = computed(() => props.disabled);

	const scriptConfig = new Script(formDatas, widgetDatas, props.otherFunc);

	const setFieldsValue = (fields) => {
		for (const key in fields) {
			formDatas.value[key] = fields[key];
		}
	};

	const setFieldsPcProps = (params) => {
		for (const code in params) {
			const item = getWidgetItem(widgetDatas.value, (itm) => itm.pcProps.code === code);
			if (JSON.stringify(item) !== '{}') {
				const pcProps = params[code];
				for (const key in pcProps) {
					item.pcProps[key] = pcProps[key];
				}
			}
		}
	};

	watch(
		widgetDatas,
		(newVal) => {
			if (props.deferSetWidgetDatas) {
				setDeferArray(deferWidgetDatas, newVal, { deep: true });
			} else {
				deferWidgetDatas.value = newVal;
			}
		},
		{
			immediate: true,
		},
	);

	onBeforeMount(() => {
		const baseConfigStore = useBaseConfigStoreWithOut(props.name);
		props.setBaseConfig && baseConfigStore.setState(props.setBaseConfig);

		props.formAttribute.onLoadScript &&
			scriptConfig &&
			scriptConfig.runScript('form-attribute', 'onLoadScript', props.formAttribute.onLoadScript).then(() => {
				props.loadFinishFunc && props.loadFinishFunc();
			});
	});

	onUnmounted(() => {
		scriptConfig.desctory();
		cancel();
	});

	// 脚本执行的类传给子组件，里边包含了执行入参
	provide('scriptConfig', scriptConfig);

	// 表单只读
	provide('formDisabled', disabled);

	// 表单配置
	provide('formAttribute', props.formAttribute);

	// 表单状态，widget | render | preview
	provide('form-status', props.formStatus);

	// 设置表单数据
	provide('setFieldsValue', setFieldsValue);

	// 表单结构数据
	provide('widgetDatas', widgetDatas);

	// 表单数据
	provide('formDatas', formDatas);

	// store唯一值
	provide('name', props.name);

	// 提供自定义控件使用
	provide('formRef', formRef);

	// field字段事件
	provide('fieldActionConfig', props.fieldActionConfig);

	// 设置表单字段属性
	provide('setFieldsPcProps', setFieldsPcProps);

	defineExpose({ formRef, scriptConfig, setFieldsValue });
</script>

<style scoped lang="scss">
	.form-ellipsis-label {
		:deep(.el-form-item) {
			.el-form-item__label_val {
				display: inline-block;
				text-align: v-bind(labelPosition);
				overflow: hidden;
				text-overflow: ellipsis;
				white-space: nowrap;
			}
		}
	}
</style>
